import {IssueTimelineItems} from '@github-ui/server-persisted-queries'
import type {IssueTimelineItemsQuery} from '@github-ui/server-persisted-queries/types/IssueTimelineItems'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

/**
 * The cached value is the issue node, not the bare connection: the optimistic-connection engine
 * locates a connection's owner by matching `id` inside the cached document.
 */
export type TimelineIssue = NonNullable<NonNullable<IssueTimelineItemsQuery['repository']>['issue']>

export type TimelineItemsConnection = NonNullable<TimelineIssue['timelineItems']>

/** The path from the cached issue node to the connection, for `applyOperation`. */
export const TIMELINE_CONNECTION_PATH: string[] = ['timelineItems']
export type TimelineItemsPageNode = NonNullable<NonNullable<TimelineItemsConnection['edges']>[number]>['node']

export type TimelineItemsPageArgs = {
  owner: string
  repo: string
  number: number
} & TimelineItemsPageParams

/**
 * Forward (`first`/`after`), backward (`last`/`before`) and deep-link (`focusText`) reads all come
 * from the one endpoint, matching the three Relay documents this replaces.
 */
export type TimelineItemsPageParams = {
  first?: number
  after?: string
  last?: number
  before?: string
  timelinePage?: number
  focusText?: string
  focusNeighborCount?: number
}

/** Prefix matching every window and cursor page for one issue. */
export function timelineItemsQueryKeyPrefix(owner: string, repo: string, number: number) {
  return ['issue-viewer', 'timeline-items', {owner, repo, number}] as const
}

export function timelineItemsQueryKey(owner: string, repo: string, number: number, params?: TimelineItemsPageParams) {
  return ['issue-viewer', 'timeline-items', {owner, repo, number}, params ?? {}] as const
}

const PARAM_NAMES: Record<keyof TimelineItemsPageParams, string> = {
  first: 'first',
  after: 'after',
  last: 'last',
  before: 'before',
  timelinePage: 'timeline_page',
  focusText: 'focus_text',
  focusNeighborCount: 'focus_neighbor_count',
}

export function timelineItemsUrl({owner, repo, number, ...params}: TimelineItemsPageArgs): string {
  const search = new URLSearchParams({queryId: IssueTimelineItems})

  for (const [key, name] of Object.entries(PARAM_NAMES)) {
    const value = params[key as keyof TimelineItemsPageParams]
    if (value !== undefined) search.set(name, String(value))
  }

  return `/${owner}/${repo}/issues/${number}/timeline_items?${search}`
}

export async function fetchTimelineItems(args: TimelineItemsPageArgs, signal?: AbortSignal): Promise<TimelineIssue> {
  const response = await verifiedFetchJSON(timelineItemsUrl(args), {signal})
  if (!response.ok) {
    throw new Error(`Timeline items fetch failed (${response.status})`)
  }

  const json = (await response.json()) as {data?: IssueTimelineItemsQuery; errors?: unknown[]}
  const issue = json.data?.repository?.issue
  if (json.errors?.length || !issue?.timelineItems) {
    throw new Error('Timeline items query returned errors')
  }

  return issue
}

export function timelineQueryOptions(owner: string, repo: string, number: number, params: TimelineItemsPageParams) {
  return {
    queryKey: timelineItemsQueryKey(owner, repo, number, params),
    queryFn: ({signal}: {signal?: AbortSignal}) => fetchTimelineItems({owner, repo, number, ...params}, signal),
    throwOnError: true as const,
  }
}

export type TimelineQueryOptions = ReturnType<typeof timelineQueryOptions>
